/**
 * @fileoverview Job Agent tab view — search bar + embedded chat + canvas
 *
 * Rendered at `/dashboard/jobs/$jobId/agent`. Combines:
 * - `UniversalSearchBar` (job scope) — typeahead + Ask AI.
 * - `UniversalChatPanel` (job scope) — multi-turn chat with the
 *   universal agent's job tools.
 * - `CanvasPanel` — AI-authored side-panel report (Phase 5). Toggled
 *   manually via the header button, or auto-opened by the agent's
 *   `openCanvas` tool. When on, the layout splits into a 50/50 chat
 *   + canvas grid (OpenAI Canvas-style).
 *
 * Height contract: the view wraps in a fixed `h-[calc(100vh-4rem)]`
 * (subtracting the dashboard header). Everything inside uses
 * `min-h-0 overflow-hidden` so the chat message list, canvas editor,
 * and proposal cards each scroll inside their own panel rather than
 * pushing the canvas out of view.
 */

import { useState, useCallback } from "react";
import type { Id } from "@cvx/_generated/dataModel";
import { UniversalChatPanel } from "./UniversalChatPanel";
import { CanvasPanel } from "./CanvasPanel";

export function JobAgentView({
  jobId,
  initialThreadId,
}: {
  jobId: Id<"jobs">;
  initialThreadId?: string;
}) {
  const [canvasOpen, setCanvasOpen] = useState(false);
  const [activeThreadId, setActiveThreadId] = useState<string | null>(
    initialThreadId ?? null,
  );
  const handleAgentRequestedCanvas = useCallback(() => setCanvasOpen(true), []);
  const toggleCanvas = useCallback(() => setCanvasOpen((v) => !v), []);
  const closeCanvas = useCallback(() => setCanvasOpen(false), []);

  // Canvas starts closed. The user can toggle it manually via the toolbar
  // button, or the agent can request it via the `openCanvas` tool.

  // The canvas toggle now lives inside the chat panel toolbar (next
  // to History / New) so there's no redundant header above the chat.
  return (
    <div className="flex h-[calc(100vh-4rem)] flex-col overflow-hidden">
      {canvasOpen ? (
        <div className="grid min-h-0 flex-1 grid-cols-1 gap-3 px-4 pb-4 pt-3 md:px-6 lg:grid-cols-2">
          <UniversalChatPanel
            scope="job"
            jobId={jobId}
            initialThreadId={initialThreadId}
            onCanvasOpenRequested={handleAgentRequestedCanvas}
            onActiveThreadChange={setActiveThreadId}
            canvasOpen={canvasOpen}
            onToggleCanvas={toggleCanvas}
            onCanvasClose={closeCanvas}
            className="min-h-0"
          />
          <CanvasPanel
            jobId={jobId}
            threadId={activeThreadId}
            className="min-h-0"
          />
        </div>
      ) : (
        <div className="flex min-h-0 flex-1 px-4 pb-4 pt-3 md:px-6">
          <UniversalChatPanel
            scope="job"
            jobId={jobId}
            initialThreadId={initialThreadId}
            onCanvasOpenRequested={handleAgentRequestedCanvas}
            onActiveThreadChange={setActiveThreadId}
            canvasOpen={canvasOpen}
            onToggleCanvas={toggleCanvas}
            onCanvasClose={closeCanvas}
            className="min-h-0 flex-1"
          />
        </div>
      )}
    </div>
  );
}
